import { CommitAudioCompanion } from '@blisspointmedia/bpm-types/dist/Creative';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getIneligibilityLabel, getIneligibilityReason } from '@Components/CreativeAllocation';
import { CreativeMap, IsciLiveOnDate } from '../../redux/creative';
import { useSetError } from '../../redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { StreamingLambdaFetch, awaitJSON } from '../../utils/fetch-utils';
import {
  CurrentCompanion,
  REMOVE_COMPANION,
  RotationItem,
  ShopData,
  WorkspaceOption
} from './streaming-creatives-constants';

export const makeNetPlatKey = ({
  network,
  platform
}: {
  network: string;
  platform: string;
}): string => `${network}-${platform}`;

export const getCurrentCompanion = (
  placementMap: ShopData['placementMap'],
  placementIds: string[],
  isci: string
): CurrentCompanion => {
  const distinct = new Set<string | null>();
  for (const id of placementIds) {
    const creative = placementMap[id]?.creatives.find(c => c.isci === isci);
    distinct.add(creative?.audio_companion_id || null);
  }
  if (distinct.size === 0) {
    return { mixed: false, distinctCount: 0, bannerCount: 0 };
  }
  if (distinct.size === 1) {
    const [only] = distinct;
    return { value: only ?? undefined, mixed: false, distinctCount: 1, bannerCount: only ? 1 : 0 };
  }
  return {
    mixed: true,
    distinctCount: distinct.size,
    bannerCount: [...distinct].filter(Boolean).length
  };
};

export const buildAudioCompanionMap = (
  rotation: Pick<RotationItem, 'isci' | 'audioCompanion'>[]
): Record<string, CommitAudioCompanion> => {
  const map: Record<string, CommitAudioCompanion> = {};
  for (const { isci, audioCompanion } of rotation) {
    if (audioCompanion === REMOVE_COMPANION) {
      map[isci] = { assetid: null };
    } else if (audioCompanion) {
      map[isci] = { assetid: audioCompanion };
    }
  }
  return map;
};

// Every creative for the selected placement context, annotated with its eligibility
// (reason/label, or none when eligible) via the shared creative-allocation engine —
// the same predicate StreamingOrderStatus uses. Returns null only while the map loads.
export const useWorkspaceOptions = (
  date: string,
  length: string | null,
  platform: string,
  adServer: string,
  dimensions: string,
  flashtalkingPlacementType: string,
  creativeMap: CreativeMap | undefined,
  colorMap: Record<string, string>,
  isciLiveOnDate: IsciLiveOnDate
): WorkspaceOption[] | null =>
  useMemo(() => {
    if (!creativeMap) {
      return null;
    }
    const iscis = Object.keys(creativeMap);
    if (!iscis.length) {
      return null;
    }

    // The shared util gates dimensions on `placementType === 'Display'`, whereas the
    // workspace historically gated on the platform string. Derive a placementType that
    // preserves the dimensions gate for Display placements that carry no explicit type.
    const placementType =
      flashtalkingPlacementType || (platform.includes('Display') ? 'Display' : undefined);

    const options: WorkspaceOption[] = iscis.map(isci => {
      const creative = creativeMap[isci];
      const ctx = {
        isci,
        date,
        platform,
        isLive: isciLiveOnDate({ isci, date }),
        length: length ?? undefined,
        dimensions: dimensions || undefined,
        placementType,
        adServer
      };
      const reason = getIneligibilityReason(creative, ctx);
      return {
        ...creative,
        isci,
        name: isci,
        creative: creative.name,
        color: colorMap[isci] || 'white',
        reason,
        disabledReason: reason ? getIneligibilityLabel(reason, creative, ctx) : undefined
      };
    });

    // Eligible creatives first, then alphabetical by ISCI within each group.
    return options.sort((a, b) => {
      if (Boolean(a.reason) !== Boolean(b.reason)) {
        return a.reason ? 1 : -1;
      }
      return a.isci.localeCompare(b.isci);
    });
  }, [
    adServer,
    colorMap,
    creativeMap,
    date,
    dimensions,
    flashtalkingPlacementType,
    isciLiveOnDate,
    length,
    platform
  ]);

export const useShopData = (): [ShopData | undefined, () => Promise<void>] => {
  const { cid: company } = useCompanyInfo();
  const setError = useSetError();

  const [shopData, setShopData] = useState<ShopData>();

  const fetchShop = useCallback(async () => {
    try {
      const res = await StreamingLambdaFetch('/creative/shop', { params: { company } });
      const { placements } = await awaitJSON<Pick<ShopData, 'placements'>>(res);
      const placementMap: ShopData['placementMap'] = {};
      for (const placement of placements) {
        placementMap[placement.id] = placement;
      }
      setShopData({ placements, placementMap });
    } catch (e) {
      setError({ message: `Couldn't fetch shop data. ${e.message}`, reportError: e });
    }
  }, [company, setError]);

  useEffect(() => {
    if (company) {
      fetchShop();
    }
  }, [company, fetchShop]);

  return [shopData, fetchShop];
};
